import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ThemeToggle from '../components/ThemeToggle';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Link de verificação inválido. Nenhum token fornecido.');
      return;
    }

    // Prevent multiple verification attempts
    if (hasVerified.current) {
      return;
    }

    const verify = async () => {
      if (hasVerified.current) return;
      
      try {
        hasVerified.current = true;
        await verifyEmail(token);
        setStatus('success');
        setMessage('Email verificado com sucesso! Você agora pode acessar todos os recursos.');

        // Redirect to decks after 3 seconds
        setTimeout(() => {
          navigate('/decks');
        }, 3000);
      } catch (error) {
        setStatus('error');
        setMessage('Falha na verificação do email. O link pode estar expirado ou inválido.');
      }
    };

    verify();
  }, [searchParams, verifyEmail, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        {/* Theme Toggle - Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        
        <div className="max-w-md w-full space-y-8 text-center">
          <LoadingSpinner />
          <p className="text-gray-600 dark:text-gray-400">Verificando seu email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            Verificação de Email
          </h2>
          
          <div className={`mt-8 p-6 rounded-lg ${
            status === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
              status === 'success' ? 'bg-green-100' : 'bg-red-100'
            } mb-4`}>
              {status === 'success' ? (
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            
            <p className={`text-sm ${
              status === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {message}
            </p>
          </div>

          <div className="mt-6 space-y-3">
            {status === 'success' ? (
              <Link
                to="/dashboard"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Ir para o Painel
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Voltar para Login
                </Link>
                <Link
                  to="/resend-verification"
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Reenviar Email de Verificação
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Footer with legal links */}
        <div className="mt-8 text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-500 dark:text-gray-400">
            <Link 
              to="/terms-of-service" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Termos de Uso
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link 
              to="/privacy-policy" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
